import React, { Fragment, useEffect } from "react";
import Loadable from "react-loadable";
import { Provider } from "react-redux";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { dom, library } from "@fortawesome/fontawesome-svg-core";
import { faAngleDown, faAngleRight } from "@fortawesome/free-solid-svg-icons";

import "./App.scss";
import store from "../redux";
import LoadingProgress from "components/loading-progress/loading-progress";
import { getConfig } from "utils/config";

import AuthGuard from "components/auth-guard/AuthGuard";
import PrivateRoute from "components/private-route/PrivateRoute";
import { OAUTH_CALLBACK_PATH } from "constants/auth";

const Redirect = Loadable({
  loader: () => import("./redirect/redirect"),
  loading: LoadingProgress,
});

const OVOModalUsahaTNC = Loadable({
  loader: () => import("./product/ovo-modalusaha-tnc"),
  loading: LoadingProgress,
});

const OVOModalUsahaRegistrationForm = Loadable({
  loader: () => import("./product/registration-form"),
  loading: LoadingProgress,
});

const NotFound = Loadable({
  loader: () => import("./not-found/not-found"),
  loading: LoadingProgress,
});

const OJKReport = Loadable({
  loader: () => import("./report/ojk"),
  loading: LoadingProgress,
});

const Main = Loadable({
  loader: () => import("./v2/main/main"),
  loading: LoadingProgress,
});

const OMUBorrower = Loadable({
  loader: () => import("./v2/omu-landing/borrower/omu-borrower"),
  loading: LoadingProgress,
});

const UnderlyingLanding = Loadable({
  loader: () => import("./v2/omu-landing/underlying/underlying"),
  loading: LoadingProgress,
});

const LeadsForm = Loadable({
  loader: () => import("./v2/leads-form/leads-form"),
  loading: LoadingProgress,
});

const AboutUsV2 = Loadable({
  loader: () => import("./v2/about-us/about-us"),
  loading: LoadingProgress,
});

const PayLaterTNC = Loadable({
  loader: () => import("./tnc/opl"),
  loading: LoadingProgress,
});

const PayLaterAutoDebitTNC = Loadable({
  loader: () => import("./tnc/opl/auto-debit"),
  loading: LoadingProgress,
});

const PayLaterLoanAgreement = Loadable({
  loader: () => import("./tnc/opl/loan-agreement"),
  loading: LoadingProgress,
});

const Health = Loadable({
  loader: () => import("./health"),
  loading: LoadingProgress,
});

const PrivacyNotice = Loadable({
  loader: () => import("./v2/privacy-notice"),
  loading: LoadingProgress,
});

const GrabModalMantul = Loadable({
  loader: () => import("./v2/grab-modal-mantul"),
  loading: LoadingProgress,
});

const Paylater = Loadable({
  loader: () => import("./v2/paylater"),
  loading: LoadingProgress,
});

const GrabModalNarikTNC = Loadable({
  loader: () => import("./tnc/grab-modal-narik"),
  loading: LoadingProgress,
});

const DataDeletion = Loadable({
  loader: () => import("./v2/data-deletion"),
  loading: LoadingProgress,
});

const RepaymentPause = Loadable({
  loader: () => import("./v2/repayment-pause"),
  loading: LoadingProgress,
});

const Login = Loadable({
  loader: () => import("./v2/login"),
  loading: LoadingProgress,
});

const Register = Loadable({
  loader: () => import("./v2/register"),
  loading: LoadingProgress,
});

const Authenticated = Loadable({
  loader: () => import("./authenticated"),
  loading: LoadingProgress,
});

const AccessDenied = Loadable({
  loader: () => import("./access-denied"),
  loading: LoadingProgress,
});

const OmuDashboard = Loadable({
  loader: () => import("./v2/omu-dashboard"),
  loading: LoadingProgress,
});

const Kyb = Loadable({
  loader: () => import("./v2/kyb"),
  loading: LoadingProgress,
});

const OmuInvoiceSubmission = Loadable({
  loader: () => import("./v2/omu-invoice-submission"),
  loading: LoadingProgress,
});

const OmuSubmissionDetail = Loadable({
  loader: () => import("./v2/omu-submission-detail"),
  loading: LoadingProgress,
});

const OmuLoanDetail = Loadable({
  loader: () => import("./v2/omu-loan-detail"),
  loading: LoadingProgress,
});

export const OMU_LOAN_DETAIL_ROUTE = "/omu/loans/:loanId";

const App = () => {
  useEffect(() => {
    dom.watch();
    library.add(faAngleDown, faAngleRight);

    document.addEventListener(
      "dragover",
      (e) => {
        e.preventDefault();
      },
      false,
    );
    document.addEventListener(
      "drop",
      (e) => {
        e.preventDefault();
      },
      false,
    );
  }, []);

  const removeButtonFlag = getConfig(
    "REMOVE_BUTTON_AND_LINK_TO_GFORM_06102022",
    true,
  );
  const isDataDeletionEnabled = getConfig(
    "IS_PERSONAL_DATA_DELETION_PAGE_ENABLED_20260312",
    false,
  );
  const isRepaymentPauseEnabled = getConfig(
    "IS_REPAYMENT_PAUSE_PAGE_ENABLED_20260930",
    false,
  );
  const isOmuDigitalizationEnabled = getConfig(
    "IS_OMU_DIGITALIZATION_PAGE_ENABLED_20260402",
    false,
  );

  return (
    <Provider store={store}>
      <Router>
        <Fragment>
          <Switch>
            <Route exact path="/" component={Main} />
            {!removeButtonFlag && (
              <Route
                exact
                path="/product/ovo-modalusaha/form"
                component={OVOModalUsahaRegistrationForm}
              />
            )}
            <Route
              exact
              path="/product/ovo-modalusaha/terms-and-conditions"
              component={OVOModalUsahaTNC}
            />
            <Route exact path="/about" component={AboutUsV2} />
            {/*<Route exact path="/redirect/:url" component={Redirect} />*/}
            <Route exact path="/report" component={OJKReport} />
            <Route exact path="/omu/landing/borrower" component={OMUBorrower} />
            <Route
              exact
              path="/omu/landing/underlying"
              component={UnderlyingLanding}
            />
            <Route exact path="/omu/leads-form" component={NotFound} />
            <Route
              exact
              path="/ovo-paylater/terms-conditions"
              component={PayLaterTNC}
            />
            <Route
              exact
              path="/ovo-paylater/terms-conditions/auto-debit"
              component={PayLaterAutoDebitTNC}
            />
            <Route
              exact
              path="/ovo-paylater/loan-agreement"
              component={PayLaterLoanAgreement}
            />
            <Route
              exact
              path="/grab-modal-narik/terms-conditions"
              component={GrabModalNarikTNC}
            />
            <Route exact path="/health" component={Health} />
            <Route exact path="/privacy-notice" component={PrivacyNotice} />
            <Route exact path="/GrabModalMantul" component={GrabModalMantul} />
            <Route exact path="/paylater" component={Paylater} />
            {isDataDeletionEnabled && (
              <Route exact path="/data-deletion" component={DataDeletion} />
            )}
            {isRepaymentPauseEnabled && (
              <Route
                exact
                path="/repayment-pause"
                component={RepaymentPause}
              />
            )}
            {isOmuDigitalizationEnabled && (
              <Route path="/omu">
                <AuthGuard>
                  <Switch>
                    <Route exact path="/omu/login" component={Login} />
                    <Route exact path="/omu/register" component={Register} />
                    <PrivateRoute
                      exact
                      path="/omu/dashboard"
                      component={OmuDashboard}
                    />
                    <PrivateRoute
                      exact
                      path="/omu/invoice"
                      component={OmuDashboard}
                    />
                    <PrivateRoute exact path="/omu/kyb" component={Kyb} />
                    <PrivateRoute exact path="/omu/invoice/new" component={OmuInvoiceSubmission} />
                    <PrivateRoute
                      exact
                      path="/omu/invoice/:submissionId"
                      component={OmuSubmissionDetail}
                    />
                    <PrivateRoute
                      exact
                      path={OMU_LOAN_DETAIL_ROUTE}
                      component={OmuLoanDetail}
                    />
                  </Switch>
                </AuthGuard>
              </Route>
            )}
            <Route
              exact
              path={OAUTH_CALLBACK_PATH}
              component={Authenticated}
            />
            <Route exact path="/access-denied" component={AccessDenied} />
            <Route component={NotFound} />
          </Switch>
        </Fragment>
      </Router>
    </Provider>
  );
};

export default App;
